// Application entry point.
//
// Migrated from the Vue 2 `new Vue({ el, router, icons, template })` bootstrap
// to the Vue 3 application instance API. Responsibilities, in order, before
// mount (see design.md "Application entry"):
//   1. createApp(App)
//   2. app.config.performance = true      (parity with Vue.config.performance)
//   3. app.use(router)
//   4. app.use(CoreuiVue)
//   5. app.provide('icons', iconsSet)     (CoreUI Vue 3 icon convention)
//   6. app.config.globalProperties.$config = config
//   7. register the shared event bus
//   8. verify #app exists, then app.mount('#app')
//
// `vue-resource` is not registered (axios replaces it) and the Vue 2
// `config.install` / `Vue.use(config)` fake-plugin indirection is removed in
// favor of a direct `globalProperties` assignment.
//
// The bootstrap logic is exported as pure, injectable functions so it can be
// unit-tested without triggering the real mount. The real mount only runs when
// this module is loaded as the application entry point (not under test).

import { createApp } from 'vue'
import App from './App'
import router from './router'
import CoreuiVue from '@coreui/vue'
import { CIcon } from '@coreui/icons-vue'
import { iconsSet } from './assets/icons/icons.js'
import { config } from './config/appConfig.js'
import eventBus from './utils/eventBus.js'

/**
 * Register a plugin with the application, surfacing an explicit error that
 * identifies the failing plugin if registration throws. A retained-plugin
 * registration failure must halt mounting (Requirement 7.4).
 *
 * @param {import('vue').App} app  The Vue 3 application instance.
 * @param {import('vue').Plugin} plugin  The plugin to register.
 * @param {string} name  Human-readable plugin name for error reporting.
 * @param {...any} options  Optional plugin options passed to `app.use`.
 * @returns {import('vue').App} The same application instance (for chaining).
 */
export function registerPlugin(app, plugin, name, ...options) {
  try {
    app.use(plugin, ...options)
  } catch (err) {
    throw new Error(
      `Failed to register plugin "${name}" during application bootstrap: ${
        err && err.message ? err.message : err
      }`
    )
  }
  return app
}

/**
 * Create and configure the root Vue 3 application instance without mounting it.
 * Performs every registration step required before mount so the returned app is
 * ready to mount. Dependencies are injectable to support unit testing.
 *
 * @param {object} [deps]
 * @param {import('vue').Component} [deps.rootComponent] Root component (App).
 * @param {import('vue').Plugin} [deps.routerPlugin] The vue-router instance.
 * @param {import('vue').Plugin} [deps.uiPlugin] The CoreUI plugin.
 * @param {object} [deps.icons] The icon set to provide.
 * @param {object} [deps.appConfig] The global `$config` object.
 * @param {object} [deps.bus] The shared event bus.
 * @returns {import('vue').App} The configured (unmounted) application instance.
 */
export function createApplication({
  rootComponent = App,
  routerPlugin = router,
  uiPlugin = CoreuiVue,
  icons = iconsSet,
  appConfig = config,
  bus = eventBus
} = {}) {
  const app = createApp(rootComponent)

  // Parity with Vue 2 `Vue.config.performance = true`.
  app.config.performance = true

  // Register retained plugins. A failure here halts bootstrap and identifies
  // the failing plugin (Requirement 7.4).
  registerPlugin(app, routerPlugin, 'vue-router')
  registerPlugin(app, uiPlugin, '@coreui/vue')

  // Provide the icon set so `CIcon` references resolve without a missing-icon
  // placeholder or console warning (Requirement 2.5).
  app.provide('icons', icons)

  // In @coreui/vue v4 the CIcon component lives in the separate
  // @coreui/icons-vue package and is not auto-registered by CoreuiVue.
  // Register it globally so `<CIcon .../>` resolves everywhere. It reads the
  // provided `icons` set above to resolve string icon names.
  app.component('CIcon', CIcon)

  // Expose the shared global config through globalProperties instead of
  // Vue.prototype (Requirements 7.1, 7.2).
  app.config.globalProperties.$config = appConfig

  // Register the shared event bus so components can access it (in addition to
  // importing the module directly).
  app.provide('eventBus', bus)
  app.config.globalProperties.$eventBus = bus

  return app
}

/**
 * Mount a configured application to the target element, halting with an
 * explicit error if the mount target does not exist (Requirement 2.7).
 *
 * @param {import('vue').App} app  A configured application instance.
 * @param {string} [selector='#app']  The mount target selector.
 * @param {Document} [doc=document]  The document to query (injectable for tests).
 * @returns {any} The mounted root component instance.
 */
export function mountApplication(app, selector = '#app', doc = document) {
  const target = doc.querySelector(selector)

  if (!target) {
    throw new Error(
      `Application mount target "${selector}" was not found in the document; ` +
        'aborting bootstrap without mounting.'
    )
  }

  return app.mount(target)
}

/**
 * Create, configure, and mount the application. This is the real entry-point
 * behavior.
 *
 * @param {object} [options]
 * @param {string} [options.selector='#app'] Mount target selector.
 * @param {object} [options.deps] Injectable dependencies for createApplication.
 * @param {Document} [options.doc] Document used to resolve the mount target.
 * @returns {{ app: import('vue').App, root: any }}
 */
export function bootstrap({ selector = '#app', deps, doc } = {}) {
  const app = createApplication(deps)
  const root = mountApplication(app, selector, doc)
  return { app, root }
}

// Run the real bootstrap when loaded as the application entry point. Guarded so
// importing this module in unit tests (task 4.2) does not trigger a real mount.
if (process.env.NODE_ENV !== 'test') {
  bootstrap()
}
